import { useState, useEffect, useCallback } from 'react';
import { getDailyPlan, getAnalytics, RoastBatch, DailyPlan, AnalyticsData, BlendStats, OvenStats } from '../api/client';

type DashboardTab = 'monitoring' | 'blends' | 'ovens';

function todayStr(): string {
  return new Date().toISOString().split('T')[0];
}

function formatDuration(seconds: number | null): string {
  if (seconds === null || seconds === undefined) return '—';
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function StatCard({ label, value, color, icon }: { label: string; value: number | string; color: string; icon: string }) {
  return (
    <div className={`${color} rounded-2xl p-6 shadow-sm`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-lg font-medium text-gray-600">{label}</p>
          <p className="text-4xl font-extrabold text-gray-800 mt-1">{value}</p>
        </div>
        <span className="text-5xl">{icon}</span>
      </div>
    </div>
  );
}

function BatchList({ title, batches, emptyText, color }: {
  title: string;
  batches: RoastBatch[];
  emptyText: string;
  color: string;
}) {
  return (
    <div className={`${color} rounded-2xl p-5 shadow-sm`}>
      <h3 className="font-bold text-xl text-gray-700 mb-3">{title} ({batches.length})</h3>
      {batches.length === 0 ? (
        <p className="text-gray-400 text-lg">{emptyText}</p>
      ) : (
        <div className="space-y-2">
          {batches.map((b) => (
            <div key={b.id} className="bg-white rounded-xl p-4 flex items-center justify-between shadow-sm">
              <div>
                <span className="font-bold text-xl text-gray-800">{b.blend_name}</span>
                <span className="mr-3 text-lg text-gray-500">{b.quantity} {b.unit === 'kg' ? 'ק״ג' : 'אצוות'}</span>
                {b.machine_id && <span className="mr-2 text-sm bg-amber-100 text-amber-800 px-2 py-0.5 rounded-lg font-medium">תנור {b.machine_id}</span>}
              </div>
              <div className="flex items-center gap-3">
                {b.status === 'in-progress' && b.timer_running && b.remaining_seconds != null && (
                  <span className={`font-mono text-xl font-bold px-3 py-1 rounded-lg ${
                    b.remaining_seconds <= 30 ? 'bg-orange-100 text-orange-700' : 'bg-amber-100 text-amber-800'
                  }`}>
                    ⏱ {formatDuration(b.remaining_seconds)}
                  </span>
                )}
                {b.status === 'in-progress' && b.timer_started_at && !b.timer_running && (
                  <span className="font-bold text-lg px-3 py-1 rounded-lg bg-red-100 text-red-700 animate-pulse">
                    🔔 הסתיים!
                  </span>
                )}
                <div className="text-sm text-gray-400">
                  {b.start_time && `התחיל: ${new Date(b.start_time).toLocaleTimeString('he-IL')}`}
                  {b.end_time && ` | הסתיים: ${new Date(b.end_time).toLocaleTimeString('he-IL')}`}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─── Daily Monitoring Tab ────────────────────────────────────────── */

function MonitoringTab({ planDate, setPlanDate }: { planDate: string; setPlanDate: (d: string) => void }) {
  const [plan, setPlan] = useState<DailyPlan | null>(null);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    try {
      const data = await getDailyPlan(planDate);
      setPlan(data);
    } catch (err) {
      console.error('Failed to load dashboard:', err);
    } finally {
      setLoading(false);
    }
  }, [planDate]);

  useEffect(() => { setLoading(true); loadData(); }, [loadData]);

  useEffect(() => {
    const interval = setInterval(loadData, 3000);
    return () => clearInterval(interval);
  }, [loadData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-amber-600"></div>
      </div>
    );
  }

  const batches = plan?.batches || [];
  const planned = batches.filter((b) => b.status === 'planned');
  const inProgress = batches.filter((b) => b.status === 'in-progress');
  const done = batches.filter((b) => b.status === 'done');
  const totalQty = batches.reduce((sum, b) => sum + b.quantity, 0);
  const doneQty = done.reduce((sum, b) => sum + b.quantity, 0);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-end">
        <input
          type="date"
          value={planDate}
          onChange={(e) => setPlanDate(e.target.value)}
          className="px-4 py-2 border-2 border-gray-300 rounded-xl text-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none"
        />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="סה״כ אצוות" value={batches.length} color="bg-white" icon="📦" />
        <StatCard label="מתוכנן" value={planned.length} color="bg-blue-50" icon="📋" />
        <StatCard label="בביצוע" value={inProgress.length} color="bg-yellow-50" icon="🔥" />
        <StatCard label="הושלם" value={done.length} color="bg-green-50" icon="✅" />
      </div>

      <div className="bg-white rounded-2xl p-6 shadow-sm">
        <div className="flex items-center justify-between mb-2">
          <span className="text-lg font-medium text-gray-600">התקדמות יומית</span>
          <span className="text-lg text-gray-500">
            {doneQty.toFixed(1)} / {totalQty.toFixed(1)} הושלמו
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-5">
          <div
            className="h-5 rounded-full bg-gradient-to-l from-green-500 to-amber-500 transition-all duration-500"
            style={{ width: `${totalQty > 0 ? (doneQty / totalQty) * 100 : 0}%` }}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <BatchList title="📋 מתוכנן" batches={planned} emptyText="אין אצוות ממתינות" color="bg-blue-50" />
        <BatchList title="🔥 בביצוע" batches={inProgress} emptyText="אין קלייה פעילה" color="bg-yellow-50" />
        <BatchList title="✅ הושלמו" batches={done} emptyText="אין אצוות שהושלמו" color="bg-green-50" />
      </div>
    </div>
  );
}

/* ─── Blend Statistics Tab ────────────────────────────────────────── */

function BlendTable({ stats }: { stats: BlendStats[] }) {
  if (stats.length === 0) {
    return <div className="text-center py-12 text-gray-400 text-xl">אין נתוני תערובות לתקופה זו</div>;
  }

  return (
    <div className="bg-white rounded-xl shadow overflow-x-auto">
      <table className="w-full text-right">
        <thead className="bg-amber-50">
          <tr>
            <th className="px-4 py-3 text-sm font-bold text-amber-900">תערובת</th>
            <th className="px-4 py-3 text-sm font-bold text-amber-900">סה״כ אצוות</th>
            <th className="px-4 py-3 text-sm font-bold text-amber-900">הושלמו</th>
            <th className="px-4 py-3 text-sm font-bold text-amber-900">מתוכננות</th>
            <th className="px-4 py-3 text-sm font-bold text-amber-900">בתהליך</th>
            <th className="px-4 py-3 text-sm font-bold text-amber-900">כמות כוללת</th>
            <th className="px-4 py-3 text-sm font-bold text-amber-900">כמות שהושלמה</th>
            <th className="px-4 py-3 text-sm font-bold text-amber-900">זמן קלייה ממוצע</th>
            <th className="px-4 py-3 text-sm font-bold text-amber-900">זמן קלייה נדרש</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {stats.map((b) => {
            const pct = b.total_batches > 0 ? Math.round((b.done_batches / b.total_batches) * 100) : 0;
            return (
              <tr key={b.blend_id} className="hover:bg-amber-50/50">
                <td className="px-4 py-3 font-bold text-lg">{b.blend_name}</td>
                <td className="px-4 py-3">{b.total_batches}</td>
                <td className="px-4 py-3">
                  <span className="inline-flex items-center gap-1">
                    {b.done_batches}
                    <span className="text-xs text-gray-400">({pct}%)</span>
                  </span>
                </td>
                <td className="px-4 py-3">{b.planned_batches}</td>
                <td className="px-4 py-3">{b.in_progress_batches}</td>
                <td className="px-4 py-3">{b.total_quantity}</td>
                <td className="px-4 py-3">{b.done_quantity}</td>
                <td className="px-4 py-3 font-mono">
                  {formatDuration(b.avg_roast_duration_seconds)}
                </td>
                <td className="px-4 py-3 font-mono">
                  {b.required_roast_time_seconds !== null
                    ? formatDuration(b.required_roast_time_seconds)
                    : '—'}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/* ─── Oven Statistics Tab ─────────────────────────────────────────── */

function OvenCard({ oven }: { oven: OvenStats }) {
  const pct = oven.total_batches > 0 ? Math.round((oven.done_batches / oven.total_batches) * 100) : 0;

  return (
    <div className="bg-white rounded-xl shadow overflow-hidden">
      <div className="bg-amber-800 text-white px-6 py-4 flex items-center justify-between">
        <h3 className="text-2xl font-bold">🔥 תנור {oven.machine_id}</h3>
        <span className="bg-amber-600 rounded-full px-4 py-1 text-lg font-bold">
          {oven.done_batches}/{oven.total_batches} אצוות
        </span>
      </div>

      <div className="grid grid-cols-2 gap-4 p-5">
        <div className="bg-green-50 rounded-lg p-3 text-center">
          <div className="text-2xl font-bold text-green-700">{oven.done_batches}</div>
          <div className="text-sm text-gray-500">הושלמו ({pct}%)</div>
        </div>
        <div className="bg-blue-50 rounded-lg p-3 text-center">
          <div className="text-2xl font-bold text-blue-700">{oven.total_quantity}</div>
          <div className="text-sm text-gray-500">כמות כוללת</div>
        </div>
        <div className="bg-amber-50 rounded-lg p-3 text-center">
          <div className="text-2xl font-bold text-amber-700 font-mono">
            {formatDuration(oven.avg_roast_duration_seconds)}
          </div>
          <div className="text-sm text-gray-500">זמן קלייה ממוצע</div>
        </div>
        <div className="bg-purple-50 rounded-lg p-3 text-center">
          <div className="text-2xl font-bold text-purple-700 font-mono">
            {formatDuration(oven.total_roast_time_seconds)}
          </div>
          <div className="text-sm text-gray-500">זמן קלייה כולל</div>
        </div>
      </div>

      {oven.blend_breakdown.length > 0 && (
        <div className="px-5 pb-5">
          <h4 className="text-sm font-bold text-gray-500 mb-2">פירוט לפי תערובת</h4>
          <div className="space-y-1">
            {oven.blend_breakdown.map((bb) => {
              const maxCount = Math.max(...oven.blend_breakdown.map((x) => x.count));
              const barPct = maxCount > 0 ? (bb.count / maxCount) * 100 : 0;
              return (
                <div key={bb.blend_name} className="flex items-center gap-3">
                  <span className="w-36 truncate text-sm font-medium">{bb.blend_name}</span>
                  <div className="flex-1 bg-gray-100 rounded-full h-5 overflow-hidden">
                    <div
                      className="bg-amber-500 h-full rounded-full transition-all"
                      style={{ width: `${barPct}%` }}
                    />
                  </div>
                  <span className="text-sm text-gray-600 w-20 text-left">
                    {bb.count} ({bb.quantity})
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function OvenPanel({ stats }: { stats: OvenStats[] }) {
  if (stats.length === 0) {
    return <div className="text-center py-12 text-gray-400 text-xl">אין נתוני תנורים לתקופה זו</div>;
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {stats.map((oven) => (
        <OvenCard key={oven.machine_id} oven={oven} />
      ))}
    </div>
  );
}

/* ─── Analytics Content (shared by blend & oven tabs) ─────────────── */

function AnalyticsContent({ tab }: { tab: 'blends' | 'ovens' }) {
  const today = todayStr();
  const thirtyDaysAgo = new Date(Date.now() - 30 * 86400000).toISOString().split('T')[0];

  const [startDate, setStartDate] = useState(thirtyDaysAgo);
  const [endDate, setEndDate] = useState(today);
  const [data, setData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getAnalytics(startDate, endDate)
      .then((d) => { if (!cancelled) setData(d); })
      .catch(console.error)
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [startDate, endDate]);

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4 justify-end">
        <label className="text-sm font-medium text-gray-600">מתאריך</label>
        <input
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          className="border rounded-lg px-3 py-2 text-lg"
        />
        <label className="text-sm font-medium text-gray-600">עד</label>
        <input
          type="date"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          className="border rounded-lg px-3 py-2 text-lg"
        />
      </div>

      {data && (
        <div className="grid grid-cols-3 gap-4">
          <StatCard label="סה״כ אצוות" value={data.total_batches} color="bg-white" icon="📦" />
          <StatCard
            label="הושלמו"
            value={data.blend_stats.reduce((s, b) => s + b.done_batches, 0)}
            color="bg-green-50"
            icon="✅"
          />
          <StatCard
            label="כמות כוללת"
            value={data.blend_stats.reduce((s, b) => s + b.total_quantity, 0).toFixed(1)}
            color="bg-blue-50"
            icon="⚖️"
          />
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-amber-600"></div>
        </div>
      ) : !data ? (
        <div className="text-center py-12 text-gray-400 text-xl">אין נתונים</div>
      ) : tab === 'blends' ? (
        <BlendTable stats={data.blend_stats} />
      ) : (
        <OvenPanel stats={data.oven_stats} />
      )}
    </div>
  );
}

/* ─── Main Dashboard ──────────────────────────────────────────────── */

function Dashboard() {
  const [tab, setTab] = useState<DashboardTab>('monitoring');
  const [planDate, setPlanDate] = useState(todayStr());

  const tabs: { key: DashboardTab; label: string; icon: string }[] = [
    { key: 'monitoring', label: 'מעקב יומי', icon: '📊' },
    { key: 'blends', label: 'סטטיסטיקות תערובות', icon: '☕' },
    { key: 'ovens', label: 'סטטיסטיקות תנורים', icon: '🔥' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-extrabold text-gray-800">לוח בקרה</h1>
      </div>

      {/* Tab bar */}
      <div className="flex gap-2 border-b-2 border-amber-200">
        {tabs.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-6 py-3 text-lg font-bold rounded-t-lg transition-colors ${
              tab === t.key
                ? 'bg-amber-800 text-white'
                : 'bg-amber-100 text-amber-800 hover:bg-amber-200'
            }`}
          >
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {tab === 'monitoring' && <MonitoringTab planDate={planDate} setPlanDate={setPlanDate} />}
      {tab === 'blends' && <AnalyticsContent tab="blends" />}
      {tab === 'ovens' && <AnalyticsContent tab="ovens" />}
    </div>
  );
}

export default Dashboard;
